import { BankDetails, BankDetailsSectionContentCms } from 'cpnl-common';
import {
    DataOverview,
    DefinitionListHorizontal,
    DefinitionListItem,
    NotificationStatus,
} from '@cp-shared-19/frontend-ui';
import { Notification } from '@vwfs-bronson/bronson-react';
import { NoConnectionNotification } from 'components/notifications/no-connection/NoConnectionNotification';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IbanNotificationType } from '../edit-view/validated-iban-input/types';
import { changeBankDetailsPath } from '../../../navigation/paths';

export type BankDetailsViewProps = {
    bankDetails?: BankDetails;
    cmsContent?: BankDetailsSectionContentCms;
    onEdit: (event: MouseEvent) => void;
    notificationType: IbanNotificationType;
};

export const BankDetailsView: React.FC<BankDetailsViewProps> = ({
    onEdit,
    cmsContent,
    bankDetails,
    notificationType,
}) => {
    const { t } = useTranslation('my-profile');

    if (!cmsContent) return null;
    if (!bankDetails?.iban) {
        return <NoConnectionNotification />;
    }

    const { notification } = cmsContent;

    const chooseNotificationText = (type: IbanNotificationType) => {
        if (type === 'Success') return notification.success;
        else if (type === 'ManualChange') return notification.manualChange;
        else if (type === '2Factor') return notification.required2Factor.text;
    };

    const bankDetailsItems: DefinitionListItem[] = [
        {
            label: t('bank-details.bank-account-label'),
            value: bankDetails.iban,
            tooltip: t('bank-details.bank-account-tooltip'),
            labelId: t('bank-details.bank-account-tooltip-aria'),
        },
        {
            label: t('bank-details.payment-method'),
            value: bankDetails?.paymentMethod,
        },
    ].filter((item) => item.value);

    const urlHref = changeBankDetailsPath();

    return (
        <DataOverview
            href={urlHref}
            buttonLabel={cmsContent?.editButton.label}
            buttonProps={{
                onClick: (event: MouseEvent): void => onEdit(event),
                'aria-label': cmsContent?.editButton.ariaLabel,
                title: cmsContent?.editButton.title,
            }}
            title={t('bank-details.sub-headline')}
            titleLevel={3}
        >
            <DefinitionListHorizontal list={bankDetailsItems} className="c-description-list--split" />
            {notificationType && (
                <Notification
                    status={notificationType === 'Success' ? NotificationStatus.success : NotificationStatus.warning}
                    title={notificationType === '2Factor' ? notification.required2Factor.headline : ''}
                    testId="notification"
                    visible
                    showCloseButton={false}
                >
                    {chooseNotificationText(notificationType)}
                </Notification>
            )}
        </DataOverview>
    );
};
